"use client"

import { motion } from "framer-motion"
import { FileText, Play } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { Name_lesson } from "@/lib/types/course/detail"
import Link from "@/components/Link"
import { useRouter } from "next/navigation"
import { courseService } from "@/lib/services/course"
import { COURSE_STATUS } from "@/lib/constants/course"

interface LessonItemProps {
	lesson: Name_lesson
	slug: string
	courseDetail: any
	// Optional props for different use cases
	isHovered?: boolean
	onMouseEnter?: () => void
	onMouseLeave?: () => void
	formatDuration?: (duration: string) => string
	isLastLesson?: boolean
	searchQuery?: string
	isActive?: boolean
}

export const LessonItem = ({
	lesson,
	slug,
	courseDetail,
	isHovered = false,
	onMouseEnter,
	onMouseLeave,
	formatDuration = (d: string) => d,
	isLastLesson = false,
	searchQuery = "",
	isActive = false,
}: LessonItemProps) => {
	const router = useRouter()

	const handleClick = async () => {
		// Check course status before allowing access
		const response = await courseService.handleCourseAction(courseDetail, () => {})
		if (response.success) {
			if (courseDetail.status.status === COURSE_STATUS.CONTINUE_LEARNING) {
				router.push(`/course/learning/${slug}/?lesson_id=${lesson.id}`)
			} else {
				router.push(response.redirectUrl || `/course/${slug}`)
			}
		} else {
			router.push(`/course/${slug}`)
		}
	}

	// If searchQuery is provided, use the learning page layout
	if (searchQuery !== undefined) {
		return (
			<motion.div
				initial={{ opacity: 0, x: -20 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ duration: 0.2 }}
				className={cn(
					"flex cursor-pointer flex-wrap items-center rounded-md p-1.5 pl-3 text-xs transition-all hover:bg-gray-50 sm:p-2 sm:pl-4 sm:text-sm",
					"group relative",
					lesson.learned ? "text-blue-600" : "text-gray-600",
					"hover:shadow-sm",
					isActive && "border border-blue-400 bg-primary/5 hover:bg-primary/10",
					isLastLesson && "border border-blue-200 bg-blue-50/50",
				)}
				id={`lesson-${lesson.id}`}
				onClick={handleClick}
			>
				<div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
					{lesson.learned ? (
						<Play className="h-3.5 w-3.5 shrink-0 text-blue-500 sm:h-4 sm:w-4" />
					) : (
						<FileText className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-primary sm:h-4 sm:w-4" />
					)}
					<span className={cn(
						"line-clamp-2 break-words sm:line-clamp-1",
						lesson.learned ? "text-blue-600" : "text-gray-600",
						isLastLesson && "font-medium"
					)}>
						{lesson.name}
					</span>
				</div>
				{lesson.duration && (
					<span className="ml-auto shrink-0 text-[10px] text-gray-500 sm:text-xs">
						{lesson.duration}
					</span>
				)}
			</motion.div>
		)
	}

	// Default layout for course page
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						variant="ghost"
						className={cn(
							"flex w-full items-center rounded-md p-2 transition-all sm:p-2.5",
							"hover:bg-gray-50/80",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
							"whitespace-normal text-left",
							isLastLesson && "border border-blue-200 bg-blue-50/50",
							lesson.learned && "text-blue-600"
						)}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onClick={handleClick}
					>
						<div className="flex w-full items-center justify-between gap-1.5 sm:gap-2">
							<div className="flex min-w-0 items-start">
								<motion.div
									animate={{
										scale: isHovered ? 1.1 : 1,
									}}
									transition={{ duration: 0.2 }}
									className="shrink-0"
								>
									{isHovered ? (
										<Play className="mr-1.5 h-3.5 w-3.5 shrink-0 text-blue-500 sm:mr-2 sm:h-4 sm:w-4" />
									) : (
										<FileText className={cn(
											"mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4",
											lesson.learned ? "text-blue-500" : "text-gray-400"
										)} />
									)}
								</motion.div>
								<span className={cn(
									"line-clamp-2 break-words font-medium text-xs sm:line-clamp-1 sm:text-sm",
									lesson.learned ? "text-blue-600" : "text-gray-600",
									isLastLesson && "font-semibold"
								)}>
									{lesson.name}
								</span>
							</div>

							{lesson.duration !== "00:00:00" && (
								<span className="ml-1.5 shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 sm:ml-2 sm:px-2 sm:py-1 sm:text-xs">
									{formatDuration(lesson.duration)}
								</span>
							)}
						</div>
					</Button>
				</TooltipTrigger>
				<TooltipContent>
					<p className="text-xs sm:text-sm">{isLastLesson ? "Continue from last lesson" : "Click to start lesson"}</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}
